import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  BarChart3,
  Users,
  Heart,
  DollarSign,
  Building2,
  PieChart as PieChartIcon
} from 'lucide-react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Area, AreaChart } from 'recharts';

export default function Analytics() {
  const [stats, setStats] = useState({});
  const [employers, setEmployers] = useState([]);
  const [selectedEmployerId, setSelectedEmployerId] = useState('');
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    try {
      const user = await api.auth.me();
      const systemAdmin = user?.user_role === 'admin' || user?.role === 'ADMIN';
      setIsSystemAdmin(systemAdmin);

      if (systemAdmin) {
        const employersData = await api.entities.Employer.list();
        setEmployers(employersData);
        const defaultEmployerId = employersData?.[0]?.id || '';
        setSelectedEmployerId(defaultEmployerId);
        if (defaultEmployerId) {
          await loadData(defaultEmployerId, true);
        } else {
          setStats({});
        }
      } else {
        await loadData(undefined, false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (employerId, systemAdmin) => {
    try {
      const useTenantFilter = Boolean(systemAdmin && employerId);

      const [employersData, employees, enrollments, products, categories] = await Promise.all([
        api.entities.Employer.list(),
        useTenantFilter ? api.entities.Employee.filter({ employer_id: employerId }) : api.entities.Employee.list(),
        useTenantFilter ? api.entities.Enrollment.filter({ employer_id: employerId }) : api.entities.Enrollment.list(),
        useTenantFilter ? api.entities.BenefitProduct.filter({ employer_id: employerId }) : api.entities.BenefitProduct.list(),
        useTenantFilter ? api.entities.BenefitCategory.filter({ employer_id: employerId }) : api.entities.BenefitCategory.list()
      ]);

    const scopedEmployers = useTenantFilter
      ? employersData.filter((e) => e.id === employerId)
      : employersData;

    if (!systemAdmin) {
      setEmployers(employersData);
      setSelectedEmployerId('');
    }

      const activeEmployees = employees.filter(e => e.is_active !== false);
      const activeEnrollments = enrollments.filter(e => e.status === 'active');
      const totalPremiums = activeEnrollments.reduce((sum, e) => sum + (e.premium_amount || 0), 0);
      
      // Enrollments by product
      const enrollmentsByProduct = products.map(p => ({
        name: p.name.length > 12 ? p.name.substring(0, 12) + '...' : p.name,
        count: activeEnrollments.filter(e => e.product_id === p.id).length,
        revenue: activeEnrollments.filter(e => e.product_id === p.id).reduce((s, e) => s + (e.premium_amount || 0), 0)
      })).filter(p => p.count > 0).sort((a, b) => b.count - a.count);

      // Enrollments by category
      const productToCategoryId = new Map(products.map((p) => [p.id, p.category_id]));
      const enrollmentsByCategoryId = new Map();

      for (const enrollment of activeEnrollments) {
        const categoryId = productToCategoryId.get(enrollment.product_id);
        if (!categoryId) continue;
        enrollmentsByCategoryId.set(categoryId, (enrollmentsByCategoryId.get(categoryId) || 0) + 1);
      }

      const enrollmentsByCategory = categories
        .map((c) => ({
          name: c.name,
          value: enrollmentsByCategoryId.get(c.id) || 0
        }))
        .filter((c) => c.value > 0);

      // Enrollment trend (based on real data)
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const now = new Date();
      const last6Months = [];
      
      for (let i = 5; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthEnrollments = activeEnrollments.filter(e => {
          if (!e.created_at) return false;
          const enrollDate = new Date(e.created_at);
          return enrollDate.getFullYear() === date.getFullYear() && 
                 enrollDate.getMonth() === date.getMonth();
        });
        
        last6Months.push({
          month: monthNames[date.getMonth()],
          enrollments: monthEnrollments.length,
          premiums: monthEnrollments.reduce((sum, e) => sum + (e.premium_amount || 0), 0)
        });
      }
      
      const trendData = last6Months;

      // Employer breakdown
      const employerBreakdown = scopedEmployers.map(emp => ({
        name: emp.company_name?.substring(0, 10) || 'Unknown',
        employees: activeEmployees.filter(e => e.employer_id === emp.id).length,
        enrollments: activeEnrollments.filter(e => e.employer_id === emp.id).length
      }));

      setStats({
        employers: scopedEmployers.length,
        employees: activeEmployees.length,
        enrollments: activeEnrollments.length,
        products: products.length,
        totalPremiums,
        enrollmentsByProduct,
        enrollmentsByCategory,
        trendData,
        employerBreakdown
      });
    } catch (err) {
      console.error('Error loading analytics data:', err);
      // Set default empty stats to prevent rendering errors
      setStats({
        employers: 0,
        employees: 0,
        enrollments: 0,
        products: 0,
        totalPremiums: 0,
        enrollmentsByProduct: [],
        enrollmentsByCategory: [],
        trendData: [],
        employerBreakdown: []
      });
    }
  };

  const handleEmployerChange = async (value) => {
    setSelectedEmployerId(value);
    setLoading(true);
    try {
      await loadData(value, true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Analytics</h1>
        <p className="text-slate-500 mt-1">
          Insights and trends for benefits enrollment
        </p>
      </div>

      {isSystemAdmin && (
        <Card>
          <CardContent className="p-4">
            <div className="grid gap-2">
              <Label>Employer</Label>
              <Select value={selectedEmployerId} onValueChange={handleEmployerChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employer" />
                </SelectTrigger>
                <SelectContent>
                  {employers.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-blue-100 text-sm">Employers</p>
                <p className="text-2xl font-bold">{stats.employers || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-emerald-100 text-sm">Employees</p>
                <p className="text-2xl font-bold">{stats.employees || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Heart className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-purple-100 text-sm">Enrollments</p>
                <p className="text-2xl font-bold">{stats.enrollments || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8 opacity-80" />
              <div>
                <p className="text-amber-100 text-sm">Monthly Premiums</p>
                <p className="text-2xl font-bold">${(stats.totalPremiums || 0).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Enrollment Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-emerald-500" />
                Enrollment Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={stats.trendData || []}>
                  <defs>
                    <linearGradient id="colorEnrollments" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip />
                  <Area type="monotone" dataKey="enrollments" stroke="#10b981" strokeWidth={2} fill="url(#colorEnrollments)" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>

        {/* Enrollments by Category */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChartIcon className="w-5 h-5 text-purple-500" />
                Enrollments by Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={stats.enrollmentsByCategory || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {(stats.enrollmentsByCategory || []).map((entry, index) => (
                      <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap justify-center gap-4 mt-4">
                {stats.enrollmentsByCategory?.map((item, index) => (
                  <div key={item.name} className="flex items-center gap-2 text-sm">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                    <span className="text-slate-600">{item.name}: {item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Enrollments by Product */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-500" />
                Top Products by Enrollment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.enrollmentsByProduct?.slice(0, 5)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" stroke="#94a3b8" />
                  <YAxis dataKey="name" type="category" width={100} stroke="#94a3b8" fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>

        {/* Employer Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-amber-500" />
                Employer Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.employerBreakdown || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip />
                  <Bar dataKey="employees" fill="#f59e0b" name="Employees" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="enrollments" fill="#10b981" name="Enrollments" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}